'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string;
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'cleanflow_consent_v1';
const APP_NAME = 'cleanflow';

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls — browser-only detection
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr';

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const usTZPrefixes = ['America/'];

  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Get current consent record from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentRecord;
  } catch (error) {
    console.error('Failed to parse consent record:', error);
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function saveConsent(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('Failed to save consent record:', error);
  }
}

/**
 * Create a new consent record
 */
export function createConsentRecord(
  region: ConsentRegion,
  choices: ConsentRecord['choices']
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true,
      ...choices,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/**
 * Check if a specific consent category is enabled
 */
export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return category === 'necessary';
  return consent.choices[category] ?? false;
}

/**
 * Log consent action (for audit trail)
 */
export function logConsentAction(
  action: 'accept_all' | 'reject_all' | 'save_preferences' | 'ccpa_dismiss',
  region: ConsentRegion
): void {
  if (typeof window === 'undefined') return;

  try {
    const timestamp = new Date().toISOString();
    console.log(`[Consent] ${action} at ${timestamp} (${region})`);
  } catch (error) {
    console.error('Failed to log consent action:', error);
  }
}
